import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { 
    getSettings, 
    saveSettings, 
    getEvolutionStatus, 
    getEvolutionQRCode, 
    logoutEvolution 
} from "../../../../services/platform_admin_service";
import { DEFAULT_CONFIG } from "../constants";

const usePlatformSettings = () => {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Evolution Status & QR Code States
    const [evolutionStatus, setEvolutionStatus] = useState("checking");
    const [qrCode, setQrCode] = useState("");
    const [loadingQr, setLoadingQr] = useState(false);

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await getSettings();
            if (result && Object.keys(result).length > 0) {
                setConfig(prev => {
                    const mergedAutomations = {
                        ...prev.automations,
                        ...(result.automations || {})
                    };
                    return {
                        ...prev,
                        ...result,
                        automations: mergedAutomations
                    };
                });
            }
        } catch (err) {
            console.error("[usePlatformSettings]", err);
            setError(err.message || "Erro ao carregar configurações.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const checkEvolStatus = useCallback(async () => {
        try {
            const result = await getEvolutionStatus();
            setEvolutionStatus(result.state || "error");
            return result.state;
        } catch (err) {
            console.error("[usePlatformSettings] Erro ao buscar status:", err);
            setEvolutionStatus("error");
            return "error";
        }
    }, []);

    // Polling WhatsApp status when QR Code is showing
    useEffect(() => {
        let interval = null;
        if (qrCode || evolutionStatus === "connecting") {
            interval = setInterval(async () => {
                const state = await checkEvolStatus();
                if (state === "open") {
                    setQrCode("");
                    toast.success("WhatsApp conectado com sucesso!");
                }
            }, 6000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [qrCode, evolutionStatus, checkEvolStatus]);

    // Initial load for Evolution status
    useEffect(() => {
        if (!loading) {
            if (config.evolutionApiUrl && config.evolutionApiKey && config.evolutionInstanceName) {
                checkEvolStatus();
            } else {
                setEvolutionStatus("not_configured");
            }
        }
    }, [loading, config.evolutionApiUrl, config.evolutionApiKey, config.evolutionInstanceName, checkEvolStatus]);

    const generateQRCode = async () => {
        try {
            setLoadingQr(true);
            setQrCode("");
            const result = await getEvolutionQRCode();
            if (result.success && result.qrcode?.base64) {
                setQrCode(result.qrcode.base64);
                setEvolutionStatus("connecting");
                toast.info("Escaneie o QR Code para conectar.");
            } else {
                toast.error("Não foi possível carregar o QR Code.");
            }
        } catch (err) {
            toast.error(err.message || "Erro ao obter QR Code da Evolution API.");
        } finally {
            setLoadingQr(false);
        }
    };

    const disconnectWhatsApp = async () => {
        try {
            setLoadingQr(true);
            await logoutEvolution();
            setQrCode("");
            setEvolutionStatus("close");
            toast.success("Instância desconectada e reiniciada com sucesso!");
        } catch (err) {
            toast.error(err.message || "Erro ao desconectar WhatsApp.");
        } finally {
            setLoadingQr(false);
        }
    };

    const updateField = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const updateNestedField = (path, value) => {
        setConfig(prev => {
            const newConfig = { ...prev };
            const keys = path.split(".");
            let current = newConfig;
            for (let i = 0; i < keys.length - 1; i++) {
                current[keys[i]] = { ...current[keys[i]] };
                current = current[keys[i]];
            }
            current[keys[keys.length - 1]] = value;
            return newConfig;
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            await saveSettings(config);
            setSuccessMessage("As configurações do SaaS foram salvas com sucesso!");
            setTimeout(() => setSuccessMessage(null), 3000);
            toast.success("Configurações salvas com sucesso!");
            
            // Re-verifica status após salvar
            if (config.evolutionApiUrl && config.evolutionApiKey && config.evolutionInstanceName) {
                checkEvolStatus();
            }
        } catch (err) {
            const msg = err.message || "Erro ao salvar configurações.";
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return { 
        config, 
        loading, 
        saving, 
        error, 
        successMessage, 
        evolutionStatus,
        qrCode,
        loadingQr,
        updateField, 
        updateNestedField,
        handleSave,
        generateQRCode,
        disconnectWhatsApp,
        checkEvolStatus
    };
};

export default usePlatformSettings;
